/**
 * Auth client — ported from Unity UnityAuthClient.
 * Handles Patreon session authentication, promo codes, and tier management.
 * Uses localStorage for persistence.
 */
import { pickBestTier } from './premiumTier';
import { openExternalUrl } from './openExternal';
import { smartFetch } from './smartFetch';

const LS_CLIENT_KEY = 'flowcore_client_key';
const LS_SAVED_TIER = 'flowcore_saved_tier';
const LS_SAVED_HASH = 'flowcore_saved_hash';
const LS_PROMO_CODES = 'flowcore_promo_codes';

// ── Client Key ──────────────────────────────────────────────────

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getOrCreateClientKey() {
  let key = localStorage.getItem(LS_CLIENT_KEY);
  if (!key) {
    key = generateUUID();
    localStorage.setItem(LS_CLIENT_KEY, key);
  }
  return key;
}

// ── MD5 Hash ────────────────────────────────────────────────────

async function computeMd5(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  // Note: Unity uses MD5 but we'll use a simple implementation
  // For compatibility with the server that expects MD5, we use a JS MD5
  return md5(input);
}

// Simple MD5 implementation (matches the Unity ComputeMd5)
function md5(string) {
  function md5cycle(x, k) {
    let a = x[0], b = x[1], c = x[2], d = x[3];
    a = ff(a, b, c, d, k[0], 7, -680876936); d = ff(d, a, b, c, k[1], 12, -389564586);
    c = ff(c, d, a, b, k[2], 17, 606105819); b = ff(b, c, d, a, k[3], 22, -1044525330);
    a = ff(a, b, c, d, k[4], 7, -176418897); d = ff(d, a, b, c, k[5], 12, 1200080426);
    c = ff(c, d, a, b, k[6], 17, -1473231341); b = ff(b, c, d, a, k[7], 22, -45705983);
    a = ff(a, b, c, d, k[8], 7, 1770035416); d = ff(d, a, b, c, k[9], 12, -1958414417);
    c = ff(c, d, a, b, k[10], 17, -42063); b = ff(b, c, d, a, k[11], 22, -1990404162);
    a = ff(a, b, c, d, k[12], 7, 1804603682); d = ff(d, a, b, c, k[13], 12, -40341101);
    c = ff(c, d, a, b, k[14], 17, -1502002290); b = ff(b, c, d, a, k[15], 22, 1236535329);
    a = gg(a, b, c, d, k[1], 5, -165796510); d = gg(d, a, b, c, k[6], 9, -1069501632);
    c = gg(c, d, a, b, k[11], 14, 643717713); b = gg(b, c, d, a, k[0], 20, -373897302);
    a = gg(a, b, c, d, k[5], 5, -701558691); d = gg(d, a, b, c, k[10], 9, 38016083);
    c = gg(c, d, a, b, k[15], 14, -660478335); b = gg(b, c, d, a, k[4], 20, -405537848);
    a = gg(a, b, c, d, k[9], 5, 568446438); d = gg(d, a, b, c, k[14], 9, -1019803690);
    c = gg(c, d, a, b, k[3], 14, -187363961); b = gg(b, c, d, a, k[8], 20, 1163531501);
    a = gg(a, b, c, d, k[13], 5, -1444681467); d = gg(d, a, b, c, k[2], 9, -51403784);
    c = gg(c, d, a, b, k[7], 14, 1735328473); b = gg(b, c, d, a, k[12], 20, -1926607734);
    a = hh(a, b, c, d, k[5], 4, -378558); d = hh(d, a, b, c, k[8], 11, -2022574463);
    c = hh(c, d, a, b, k[11], 16, 1839030562); b = hh(b, c, d, a, k[14], 23, -35309556);
    a = hh(a, b, c, d, k[1], 4, -1530992060); d = hh(d, a, b, c, k[4], 11, 1272893353);
    c = hh(c, d, a, b, k[7], 16, -155497632); b = hh(b, c, d, a, k[10], 23, -1094730640);
    a = hh(a, b, c, d, k[13], 4, 681279174); d = hh(d, a, b, c, k[0], 11, -358537222);
    c = hh(c, d, a, b, k[3], 16, -722521979); b = hh(b, c, d, a, k[6], 23, 76029189);
    a = hh(a, b, c, d, k[9], 4, -640364487); d = hh(d, a, b, c, k[12], 11, -421815835);
    c = hh(c, d, a, b, k[15], 16, 530742520); b = hh(b, c, d, a, k[2], 23, -995338651);
    a = ii(a, b, c, d, k[0], 6, -198630844); d = ii(d, a, b, c, k[7], 10, 1126891415);
    c = ii(c, d, a, b, k[14], 15, -1416354905); b = ii(b, c, d, a, k[5], 21, -57434055);
    a = ii(a, b, c, d, k[12], 6, 1700485571); d = ii(d, a, b, c, k[3], 10, -1894986606);
    c = ii(c, d, a, b, k[10], 15, -1051523); b = ii(b, c, d, a, k[1], 21, -2054922799);
    a = ii(a, b, c, d, k[8], 6, 1873313359); d = ii(d, a, b, c, k[15], 10, -30611744);
    c = ii(c, d, a, b, k[6], 15, -1560198380); b = ii(b, c, d, a, k[13], 21, 1309151649);
    a = ii(a, b, c, d, k[4], 6, -145523070); d = ii(d, a, b, c, k[11], 10, -1120210379);
    c = ii(c, d, a, b, k[2], 15, 718787259); b = ii(b, c, d, a, k[9], 21, -343485551);
    x[0] = add32(a, x[0]); x[1] = add32(b, x[1]); x[2] = add32(c, x[2]); x[3] = add32(d, x[3]);
  }
  function cmn(q, a, b, x, s, t) { a = add32(add32(a, q), add32(x, t)); return add32((a << s) | (a >>> (32 - s)), b); }
  function ff(a, b, c, d, x, s, t) { return cmn((b & c) | ((~b) & d), a, b, x, s, t); }
  function gg(a, b, c, d, x, s, t) { return cmn((b & d) | (c & (~d)), a, b, x, s, t); }
  function hh(a, b, c, d, x, s, t) { return cmn(b ^ c ^ d, a, b, x, s, t); }
  function ii(a, b, c, d, x, s, t) { return cmn(c ^ (b | (~d)), a, b, x, s, t); }
  function md51(s) {
    let n = s.length, state = [1732584193, -271733879, -1732584194, 271733878], i;
    for (i = 64; i <= n; i += 64) md5cycle(state, md5blk(s.substring(i - 64, i)));
    s = s.substring(i - 64);
    const tail = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0];
    for (i = 0; i < s.length; i++) tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3);
    tail[i >> 2] |= 0x80 << ((i % 4) << 3);
    if (i > 55) { md5cycle(state, tail); for (i = 0; i < 16; i++) tail[i] = 0; }
    tail[14] = n * 8;
    md5cycle(state, tail);
    return state;
  }
  function md5blk(s) {
    const md5blks = [];
    for (let i = 0; i < 64; i += 4) {
      md5blks[i >> 2] = s.charCodeAt(i) + (s.charCodeAt(i + 1) << 8) + (s.charCodeAt(i + 2) << 16) + (s.charCodeAt(i + 3) << 24);
    }
    return md5blks;
  }
  const hex_chr = '0123456789abcdef'.split('');
  function rhex(n) {
    let s = '';
    for (let j = 0; j < 4; j++) s += hex_chr[(n >> (j * 8 + 4)) & 0x0f] + hex_chr[(n >> (j * 8)) & 0x0f];
    return s;
  }
  function hex(x) { for (let i = 0; i < x.length; i++) x[i] = rhex(x[i]); return x.join(''); }
  function add32(a, b) { return (a + b) & 0xffffffff; }
  return hex(md51(string));
}

// ── Promo Codes ─────────────────────────────────────────────────

function loadPromoCodes() {
  try {
    const raw = localStorage.getItem(LS_PROMO_CODES);
    if (!raw) return [];
    const codes = JSON.parse(raw);
    if (!Array.isArray(codes)) return [];
    // Clean expired
    const now = new Date();
    return codes.filter((p) => {
      if (!p.expiresAt) return true;
      try { return new Date(p.expiresAt) > now; } catch { return true; }
    });
  } catch {
    return [];
  }
}

function savePromoCodes(codes) {
  const now = new Date();
  const clean = codes.filter((p) => {
    if (!p.expiresAt) return true;
    try { return new Date(p.expiresAt) > now; } catch { return true; }
  });
  localStorage.setItem(LS_PROMO_CODES, JSON.stringify(clean));
}

function bestPromoTier(tierOrder) {
  const codes = loadPromoCodes();
  let best = '';
  for (const p of codes) {
    if (p.tier) best = pickBestTier(best, p.tier, tierOrder);
  }
  return best;
}

// ── Public API ──────────────────────────────────────────────────

/**
 * Get the current effective tier (max of auth tier + best promo tier).
 */
export function getCurrentTier(tierOrder) {
  const authTier = localStorage.getItem(LS_SAVED_TIER) || '';
  const promoTier = bestPromoTier(tierOrder);
  return pickBestTier(authTier, promoTier, tierOrder) || '';
}

/**
 * Get saved promo codes list.
 */
export function getSavedPromoCodes() {
  return loadPromoCodes().map((p) => p.code);
}

/**
 * Check session with auth server.
 * @param {string} authEndpoint - POST endpoint
 * @param {Object} options
 * @param {boolean} options.openUrl - If not authenticated, open auth URL in browser
 * @param {string[]} options.tierOrder - Tier names for comparison
 * @returns {{ authenticated: boolean, tier: string, user: Object|null }}
 */
export async function checkSession(authEndpoint, { openUrl = true, tierOrder = [] } = {}) {
  if (!authEndpoint) return { authenticated: false, tier: '', user: null };

  const clientKey = getOrCreateClientKey();

  try {
    const res = await smartFetch(authEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_key: clientKey })
    });

    if (!res.ok) return { authenticated: false, tier: getCurrentTier(tierOrder), user: null };

    const resp = await res.json();

    if (resp.authenticated) {
      const firstTier = resp.entitled_tiers?.[0]?.title || '';
      const tierKey = String(firstTier).trim().toLowerCase();
      const isEmptyTier = !tierKey || tierKey === 'null' || tierKey === 'free';

      // Edge case: Patreon confirmed the login but the linked account has
      // no paid entitlement (free member, cancelled pledge, etc.). The
      // server will happily keep returning authenticated:true for this
      // clientKey forever — with no auth_url — so the UI would cache a
      // stuck session and could never re-open the Patreon auth flow.
      //
      // Treat this as 'not linked': don't persist anything, rotate the
      // clientKey so the server no longer recognises us, and retry once
      // with the fresh key to grab an auth_url we can open right away.
      if (isEmptyTier) {
        localStorage.removeItem(LS_SAVED_TIER);
        localStorage.removeItem(LS_SAVED_HASH);
        localStorage.removeItem(LS_CLIENT_KEY);
        const freshKey = getOrCreateClientKey();
        if (openUrl) {
          try {
            const retry = await smartFetch(authEndpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ game_key: freshKey })
            });
            if (retry.ok) {
              const retryResp = await retry.json();
              if (retryResp && retryResp.auth_url) {
                openExternalUrl(retryResp.auth_url);
              }
            }
          } catch (_) { /* ignore — user can tap again */ }
        }
        return {
          authenticated: false,
          tier: getCurrentTier(tierOrder),
          user: null,
          noEntitlement: true
        };
      }

      // Verify hash: md5("{clientKey} + {tier} + 123")
      const raw = `${clientKey} + ${firstTier} + 123`;
      const hash = md5(raw);

      if (resp.hash && hash.toLowerCase() === resp.hash.toLowerCase()) {
        localStorage.setItem(LS_SAVED_TIER, firstTier);
        localStorage.setItem(LS_SAVED_HASH, hash);
      }

      return {
        authenticated: true,
        tier: getCurrentTier(tierOrder),
        user: resp.user || null
      };
    } else {
      if (openUrl && resp.auth_url) {
        openExternalUrl(resp.auth_url);
      }
      return {
        authenticated: false,
        tier: getCurrentTier(tierOrder),
        user: null
      };
    }
  } catch (err) {
    console.warn('[AuthClient] Session check failed:', err.message);
    return { authenticated: false, tier: getCurrentTier(tierOrder), user: null };
  }
}

/**
 * Apply a promo code.
 * @returns {{ success: boolean, tier: string }}
 */
export async function applyPromoCode(promoEndpoint, code, tierOrder = []) {
  if (!promoEndpoint || !code?.trim()) return { success: false, tier: '' };

  try {
    const res = await smartFetch(promoEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim() })
    });

    if (!res.ok) return { success: false, tier: '' };

    const resp = await res.json();
    const tierRaw = resp.tier_name || resp.tier || '';
    const expires = resp.expires_at || resp.expiresAt || '';

    if (!tierRaw) return { success: false, tier: '' };

    // Save promo locally
    const codes = loadPromoCodes();
    const existing = codes.findIndex((p) => p.code === code.trim());
    const entry = { code: code.trim(), tier: tierRaw, expiresAt: expires };
    if (existing >= 0) codes[existing] = entry;
    else codes.push(entry);
    savePromoCodes(codes);

    return { success: true, tier: getCurrentTier(tierOrder) };
  } catch (err) {
    console.warn('[AuthClient] Apply promo failed:', err.message);
    return { success: false, tier: '' };
  }
}

/**
 * Get promo code info.
 * @returns {{ ok: boolean, tierName: string, expiresAt: string, timeRemaining: string }}
 */
export async function getPromoInfo(promoInfoEndpoint, code) {
  if (!promoInfoEndpoint || !code?.trim()) return { ok: false, tierName: '', expiresAt: '', timeRemaining: '' };

  try {
    let url = promoInfoEndpoint;
    if (url.includes('{code}')) {
      url = url.replace('{code}', encodeURIComponent(code.trim()));
    } else {
      if (!url.endsWith('/')) url += '/';
      url += encodeURIComponent(code.trim());
    }

    const res = await smartFetch(url);
    if (!res.ok) return { ok: false, tierName: '', expiresAt: '', timeRemaining: '' };

    const resp = await res.json();
    const tierName = resp.tier_name || resp.tier || '';
    const expiresAt = resp.expires_at || resp.expiresAt || '';
    const timeRemaining = getTimeRemainingString(expiresAt);

    return { ok: !!tierName, tierName, expiresAt, timeRemaining };
  } catch {
    return { ok: false, tierName: '', expiresAt: '', timeRemaining: '' };
  }
}

/**
 * Clear session & promo codes (full reset).
 */
export function clearSession() {
  localStorage.removeItem(LS_CLIENT_KEY);
  localStorage.removeItem(LS_SAVED_TIER);
  localStorage.removeItem(LS_SAVED_HASH);
  localStorage.removeItem(LS_PROMO_CODES);
  // Generate new client key
  getOrCreateClientKey();
}

/**
 * Log out — Unity-parity: rotates the session client key only. Saved tier
 * and hash are intentionally kept in localStorage (they become functionally
 * invalid against the new client key and will be replaced on the next
 * successful checkSession). The UI should immediately treat the player as
 * Free by pushing '' into its own tier state, not by reading back from
 * localStorage.
 */
export function logOut() {
  localStorage.removeItem(LS_CLIENT_KEY);
  getOrCreateClientKey();
}

function getTimeRemainingString(expiresAt) {
  if (!expiresAt) return '';
  try {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const ms = expiryDate - now;
    if (ms <= 0) return '';
    const parts = [];
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (parts.length === 0 && ms > 0) parts.push(`${Math.floor(ms / 1000)}s`);
    return parts.join(' ');
  } catch {
    return '';
  }
}
